import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable, throwError, BehaviorSubject, Subscription, interval } from 'rxjs';
import { tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';

import { LoginModel, RefreshGrantModel, AuthorizationCodeModel } from '../models';

const AUTH_TOKEN = 'a_t';

export class UserInformationModel {
  displayName: string;
  email?: string;
  permission: Array<number> | number;
  role: Array<string>;
  sub: string;
  exp: number;
  responseRequired: string;
}

export class OpenIdResponseModel {
  //  tslint:disable-next-line:variable-name
  token_type: string;
  //  tslint:disable-next-line:variable-name
  access_token: string;
  //  tslint:disable-next-line:variable-name
  expires_in: number;
  //  tslint:disable-next-line:variable-name
  refresh_token: string;
  //  tslint:disable-next-line:variable-name
  refresh_token_expires_in: number;
  //  tslint:disable-next-line:variable-name
  scope: string;
}

const secondsInterval = interval(1000);

@Injectable()
export class AuthenticationService {

  private jwtHelper = new JwtHelperService();
  public accessToken: string;
  private _refreshToken: string;
  private _jwtToken: UserInformationModel;
  public userChanged = new BehaviorSubject<UserInformationModel>(null);
  private timer$: Subscription;

  public get isAuthenticated(): boolean {
    return this.accessToken && !this.jwtHelper.isTokenExpired(this.accessToken)
      || (this._refreshToken && !this.jwtHelper.isTokenExpired(this._refreshToken));
  }

  public get isAccessTokenExpired(): boolean {
    return this.accessToken && this.jwtHelper.isTokenExpired(this.accessToken);
  }

  public get isResponseRequired(): boolean {
    return this._jwtToken && !!this._jwtToken.responseRequired;
  }

  private setAuthentication(authResponse: OpenIdResponseModel) {
    if (authResponse) {
      localStorage.setItem(AUTH_TOKEN, JSON.stringify(authResponse));
      this.accessToken = authResponse.access_token;
      this._refreshToken = authResponse.refresh_token;
      this._jwtToken = this.jwtHelper.decodeToken(authResponse.access_token);
      this.userChanged.next(this._jwtToken);
      this.clearTimer();
      this.timer$ = secondsInterval
        .subscribe((_) => {
          if (this.jwtHelper.isTokenExpired(this.accessToken)
            && this.jwtHelper.isTokenExpired(this._refreshToken)) {
            this.logout();
          }
        });
    } else {
      this.clearAuthentication();
    }
  }

  public clearAuthentication() {
    localStorage.removeItem(AUTH_TOKEN);
    this.accessToken = null;
    this._refreshToken = null;
    this._jwtToken = null;
    this.userChanged.next(this._jwtToken);
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer$) {
      this.timer$.unsubscribe();
      this.timer$ = null;
    }
  }

  constructor(
    private router: Router,
    private httpService: HttpClient) {

    //  Load auth token
    const token = localStorage[AUTH_TOKEN];
    if (token) {
      this.setAuthentication(JSON.parse(token));
    }

    window.addEventListener('storage', (ev: StorageEvent) => {
      if (ev.key === AUTH_TOKEN) {
        if (ev.newValue === null) {
          this.clearAuthentication();
        } else {
          this.setAuthentication(JSON.parse(ev.newValue) as OpenIdResponseModel);
        }
      }
    });
  }

  public requestToken(username: string, password: string, identityProvider?: string): Observable<{} | Response> {
    if (identityProvider) {
      return this.sendAuthenticationRequest({
        username,
        password,
        identity_provider: identityProvider,
      }, 'password');
    } else {
      return this.sendAuthenticationRequest({
        username,
        password
      }, 'password');
    }
  }

  public externalLogin(code: string, identityProvider: string) {
    return this.sendAuthenticationRequest({
      code,
      identity_provider: identityProvider
    }, 'authorization_code');
  }

  public refreshToken(): Observable<{} | Response> {
    if (this._refreshToken) {
      return this.sendAuthenticationRequest({
        refresh_token: this._refreshToken
      }, 'refresh_token');
    }
    return throwError({ error_description: 'Refresh token is not set' });
  }

  private sendAuthenticationRequest(
    data: LoginModel | RefreshGrantModel | AuthorizationCodeModel,
    grantType: string,
    url: string = '/connect/token'): Observable<{} | Response> {

    return this.httpService.post(
      url,
      this.encodeObjectToParams({
        ...data,
        grant_type: grantType,
        scope: ['offline_access'],   //  offline_access is required for a refresh token
      }),
      {
        headers: new HttpHeaders()
          .set('Content-Type', 'application/x-www-form-urlencoded')
      }).pipe(
        tap((result: any) => {
          this.setAuthentication(result);
        }),
      );
  }

  public developerLogin(username: string): Observable<{} | Response> {
    return this.httpService.get(`/developer/connect/${username}`)
      .pipe(
        tap(
          (result: OpenIdResponseModel) => {
            this.setAuthentication(result);
          }),
      );
  }

  logoutHandler = (returnUrl?: string) => {
    this.router.navigate(['/logout'], {
      queryParams: {
        return: returnUrl || this.router.url
      }
    });
  }

  public setLogoutHandler(handler: (_?: string) => void) {
    this.logoutHandler = handler;
  }

  public logout(returnUrl?: string) {
    this.clearAuthentication();
    this.logoutHandler(returnUrl);
  }

  public inRoles(roles: Array<string>): boolean {
    if (this._jwtToken && this._jwtToken.role && roles) {
      return roles.some(r => this._jwtToken.role.includes(r));
    }
    return false;
  }

  public hasPermission(permissions: Array<number>): boolean {
    if (this.isAuthenticated && this._jwtToken && this._jwtToken.permission && permissions) {
      return permissions.some((permission: number | Array<number>) => {
        const userPermission = Array.isArray(this._jwtToken.permission) ? this._jwtToken.permission : [this._jwtToken.permission];
        const resourcePermission = Array.isArray(permission) ? permission : [permission];
        return resourcePermission.every((p) => userPermission.includes(p));
      });
    } else if (this.isAuthenticated && this._jwtToken && !permissions) {
      return true;
    }
    return false;
  }

  private encodeObjectToParams(obj: any): string {
    return Object.keys(obj)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
      .join('&');
  }
}
